import React, {lazy} from "react";
import {Navigate} from "react-router-dom";
import {routes} from "../utils/routes";
import {DIRECTOR_ROLE_ID, DISPATCHER_ROLE_ID, DRIVER_ROLE_ID} from "../models/IUser";
import {Box} from "@mui/material";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOn from '@mui/icons-material/LocationOn';
import HistoryIcon from '@mui/icons-material/History';
import SensorsIcon from '@mui/icons-material/Sensors';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import BusAlertIcon from '@mui/icons-material/BusAlert';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DashboardIcon from '@mui/icons-material/Dashboard';

export interface IRouteConfig {
    icon?: React.ReactNode;
    path: string;
    element: React.ReactNode;
    label: string; // Название для меню
    showInMenu?: boolean; // Показывать в меню или нет
    showInDownMenu?: boolean; // Показывать в меню или нет
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    /** Роут виден только пользователям с role_id из списка (см. models/IUser). */
    requiresRoles?: number[];
    /**
     * Роут рендерится ВНЕ оболочки приложения — без SideMenu, AppNavbar и
     * уведомлений, до всякой проверки авторизации.
     *
     * Нужен публичным страницам по токену: у них собственный layout, и
     * посетитель не должен видеть навигацию по чужому кабинету, даже если в
     * этой вкладке остался чей-то access_token (см. App.tsx).
     */
    standalone?: boolean;
    requiresTariff?: string; // 'pro' | 'basic' | etc.
    children?: IRouteConfig[];
}

// Каждая страница — отдельный чанк
const FleetDashboardPage = lazy(() => import("../features/fleet_dashboard/ui/FleetDashboardPage"));
const MapPage = lazy(() => import("../features/map/ui/MapPage"));
const MapGlPage = lazy(() => import("../features/map_gl/ui/MapGlPage"));
const MapHistoryPage = lazy(() => import("../features/map_history/ui/MapHistoryPage"));
const MapHistoryGlPage = lazy(() => import("../features/map_history_gl/ui/MapHistoryGlPage"));
const MachineryTelemetryPage = lazy(() => import("../features/machinery_telemetry/ui/MachineryTelemetryPage"));
const MachineryPage = lazy(() => import("../features/machinery/ui/MachineryPage"));
const MachineryDetailsPage = lazy(() => import("../features/machinery/ui/details/MachineryDetailsPage"));
const MachineryAddNewPage = lazy(() => import("../features/machinery/ui/MachineryAddNewPage"));
const MaintenancePage = lazy(() => import("../features/maintenance/ui/MaintenancePage"));
const DispatchPage = lazy(() => import("../features/dispatch/ui/DispatchPage"));
const DriverDayPage = lazy(() => import("../features/dispatch/ui/DriverDayPage"));
const SharePage = lazy(() => import("../features/dispatch/ui/SharePage"));
const SharedGroupPage = lazy(() => import("../features/machinery_groups/ui/SharedGroupPage"));
const GroupReportPage = lazy(() => import("../features/machinery_groups/ui/GroupReportPage"));
const GroupReportsPage = lazy(() => import("../features/machinery_groups/ui/GroupReportsPage"));
const TaskAddNewPage = lazy(() => import("../features/machinery_tasks/ui/TaskAddNewPage"));
const TaskDetailsPage = lazy(() => import("../features/machinery_tasks/ui/TaskDetailsPage"));
const TasksPage = lazy(() => import("../features/machinery_tasks/ui/TasksPage"));
const ProblemsPage = lazy(() => import("../features/machinery_problems/ui/ProblemsPage"));
const MachineryCommentsPage = lazy(() => import("../features/machinery_comments/ui/MachineryCommentsPage"));
const TrackersPage = lazy(() => import("../features/trackers/ui/TrackersPage"));
const ClientsPage = lazy(() => import("../features/companies/ui/ClientsPage"));
const TariffsPage = lazy(() => import("../features/tariff/ui/TariffsPage"));
const MLMonitorPage = lazy(() => import("../features/ml_monitor/ui/MLMonitorPage"));
const ErrorCodePage = lazy(() => import("../features/errorCode/ui/ErrorCodePage"));
const AddErrorCodePage = lazy(() => import("../features/errorCode/ui/AddErrorCodePage"));
const TechnicalLiteraturePage = lazy(() => import("../features/technical_literature/ui/TechnicalLiteraturePage"));
const LoginForm = lazy(() => import("../features/auth/ui/LoginForm"));
const RegisterForm = lazy(() => import("../features/auth/ui/RegisterForm"));
const Profile = lazy(() => import("../features/auth/ui/Profile"));
const UsersPage = lazy(() => import("../features/users/ui/UsersPage"));
const ClientInvoicesAdminPage = lazy(
    () => import("../features/client_invoice/ui/ClientInvoicesAdminPage")
);
const ForgotPasswordForm = lazy(() => import("../features/password_reset/ui/ForgotPasswordForm"));
const DrivingEventsPage = lazy(() => import("../features/driving_events/ui/DrivingEventsPage"));
const InviteRegisterPage = lazy(() => import("../features/invites/ui/InviteRegisterPage"));
const BackupPage = lazy(() => import("../features/backup/ui/BackupPage"));
const LegalDocumentPage = lazy(() => import("../features/legal/ui/LegalDocumentPage"));
const MachineryAdminPage = lazy(() => import("../features/machinery/ui/MachineryAdminPage"));
const MachineryAdminMapPage = lazy(() => import("../features/machinery/ui/MachineryAdminMapPage"));

export const routesConfig: IRouteConfig[] = [
    {
        icon: <DashboardIcon/>,
        path: routes.fleetDashboard,
        element: <FleetDashboardPage/>,
        label: "Сводка",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <LocationOn/>,
        path: routes.map,
        element: <MapPage/>,
        label: "Карта",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <LocationOn/>,
        path: routes.mapGl,
        element: <MapGlPage/>,
        label: "Карта (GL)",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <HistoryIcon/>,
        path: routes.mapHistory,
        element: <MapHistoryPage/>,
        label: "История",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <HistoryIcon/>,
        path: routes.mapHistoryGl,
        element: <MapHistoryGlPage/>,
        label: "История (GL)",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <SensorsIcon/>,
        path: routes.machinery_telemetry,
        element: (<Box sx={{mt: {xs: -3, md: 0}}}>
            <MachineryTelemetryPage/>
        </Box>),
        label: "Телеметрия",
        showInMenu: true,
        requiresAuth: true,
        requiresTariff: "pro",
    },
    {
        icon: <LocalShippingIcon/>,
        path: routes.machinery,
        element: <MachineryPage/>,
        label: "Список Техники",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <CalendarMonth/>,
        path: routes.machineryMaintenance,
        element: <MaintenancePage/>,
        label: "Тех. обсл.",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        path: routes.dispatch,
        element: <DispatchPage/>,
        label: "Диспетчеризация",
        showInMenu: true,
        requiresAuth: true,
        requiresRoles: [DIRECTOR_ROLE_ID, DISPATCHER_ROLE_ID],
    },
    {
        path: routes.driverDay,
        element: <DriverDayPage/>,
        label: "Мой день",
        showInMenu: true,
        requiresAuth: true,
        requiresRoles: [DRIVER_ROLE_ID],
    },
    {
        // Отчёты по группам — основная точка входа, пункт бокового меню.
        path: routes.groupReports,
        element: <GroupReportsPage/>,
        label: "Отчёты",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        // Старый адрес отчёта: редирект на /reports/groups?group=<id>.
        // Кнопка «Отчёт» в диалоге групп и уже разосланные ссылки работают.
        path: routes.machineryGroupReport,
        element: <GroupReportPage/>,
        label: "Отчёт по группе",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        // Гостевая ссылка на группу техники: живая карта без авторизации,
        // со своим минимальным layout'ом (ни SideMenu, ни AppNavbar).
        path: routes.shareGroup,
        element: <SharedGroupPage/>,
        label: "Группа техники",
        showInMenu: false,
        requiresAuth: false,
        standalone: true,
    },
    {
        // Публичное отслеживание по токену (ТЗ 5.6): без авторизации,
        // в меню не показывается. Рендерится в обеих ветках App
        // (для анонима — без layout).
        path: routes.share,
        element: <SharePage/>,
        label: "Отслеживание",
        showInMenu: false,
        requiresAuth: false,
        standalone: true,
    },
    {
        icon: <GroupIcon/>,
        path: routes.users,
        element: <UsersPage/>,
        label: "Сотрудники",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <GroupIcon/>,
        path: routes.driving_events,
        element: <DrivingEventsPage/>,
        label: "Стиль вождения",
        showInMenu: true,
        requiresAuth: true,
    },
    {
        icon: <ReceiptLongIcon/>,
        path: routes.clientInvoices,
        element: <ClientInvoicesAdminPage/>,
        label: "Счета (адм)",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.trackers,
        element: <TrackersPage/>,
        label: "Трекеры (адм)",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.clients,
        element: <ClientsPage/>,
        label: "Клиенты (адм)",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.tariffs,
        element: <TariffsPage/>,
        label: "Тарифы (адм)",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.ml_monitor,
        element: <MLMonitorPage/>,
        label: "ML монитор (адм)",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.backup,
        element: <BackupPage/>,
        label: "Бекапы",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.machinery_admin,
        element: <MachineryAdminPage/>,
        label: "Техника",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    {
        icon: <GpsFixedIcon/>,
        path: routes.machinery_admin_map,
        element: <MachineryAdminMapPage/>,
        label: "Карта техники",
        showInMenu: true,
        requiresAuth: true,
        requiresAdmin: true,
    },
    /*
     {
         icon: <GradingIcon/>,
         path: routes.invoices,
         element: <InvoicesPage/>,
         label: "Счета",
         showInMenu: true
     },
     {
         icon: <HomeRoundedIcon/>,
         path: routes.invoicesDetails,
         element: <InvoiceDetailsPage/>,
         label: "Подробности",
         showInMenu: false
     },
     {
         icon: <HomeRoundedIcon/>,
         path: routes.invoicesAddNew,
         element: <InvoiceAddNewPage/>,
         label: "Новый счёт",
         showInMenu: false
     },
     {
         icon: <AirlineSeatIndividualSuiteIcon/>,
         path: routes.suppliers,
         element: <SuppliersPage/>,
         label: "Поставщики",
         showInMenu: true
     },
     {
         icon: <LocalShippingIcon/>,
         path: routes.shipments,
         element: <ShipmentsPage/>,
         label: "Отгрузки",
         showInMenu: true
     },
     {
         icon: <HomeRoundedIcon/>,
         path: routes.shipmentsAddNew,
         element: <ShipmentsAddNewPage/>,
         label: "Новая отгрузка",
         showInMenu: false
     },
     {
         icon: <ShoppingBasketIcon/>,
         path: routes.orders,
         element: <OrdersPage/>,
         label: "Заявки",
         showInMenu: true
     },
     {
         icon: <HomeRoundedIcon/>,
         path: routes.ordersAddNew,
         element: <OrdersAddNewPage/>,
         label: "Новая заявка",
         showInMenu: false
     },
     {
         icon: <HomeRoundedIcon/>,
         path: routes.ordersDetails,
         element: <OrderDetailsPage/>,
         label: "Подробности",
         showInMenu: false
     },*/

    {
        icon: <HomeRoundedIcon/>,
        path: routes.login,
        element: <LoginForm/>,
        label: "Вход",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.register,
        element: <RegisterForm/>,
        label: "Регистрация",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        // Сюда ведут ссылки из писем-приглашений (см. InviteService.buildInviteUrl).
        // На бэке URL формируется как `${FRONTEND_URL}/register-by-invite?invite=...`.
        // Страница сама читает query-параметр `invite` через useSearchParams.
        icon: <HomeRoundedIcon/>,
        path: routes.registerByInvite,
        element: <InviteRegisterPage/>,
        label: "Регистрация по приглашению",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.profile,
        element: <Profile/>,
        label: "Профиль",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.legalPrivacy,
        element: <LegalDocumentPage docType="privacy_policy"/>,
        label: "Политика конфиденциальности",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.legalConsent,
        element: <LegalDocumentPage docType="pd_consent"/>,
        label: "Согласие на обработку ПДн",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.legalTerms,
        element: <LegalDocumentPage docType="terms_of_use"/>,
        label: "Пользовательское соглашение",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.legalCookies,
        element: <LegalDocumentPage docType="cookie_policy"/>,
        label: "Политика использования файлов cookie",
        showInMenu: false,
        requiresAuth: false,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.forgotPassword,
        element: <ForgotPasswordForm/>,
        label: "Сброс пароля",
        showInMenu: false,
    },

    /* {
         icon: <AgricultureIcon/>,
         path: routes.machinery,
         element: <MachineryPage/>,
         label: "Техника",
         showInMenu: true,
         children: [
             {
                 path: routes.machinery,
                 element: <MachineryPage/>,
                 label: "Список",
                 showInMenu: true,
             },
             {
                 path: routes.machineryMaintenance,
                 element: <MachineryMaintenancePage/>,
                 label: "Календарь ТО",
                 showInMenu: true,
             },
             {
                 path: routes.machineryProblems,
                 element: <ProblemsPage/>,
                 label: "Проблемы",
                 showInMenu: true,
             },
             {
                 path: routes.machineryTasks,
                 element: <TasksPage/>,
                 label: "Задачи",
                 showInMenu: true,
             },
             {
                 path: routes.machineryComments,
                 element: <MachineryCommentsPage/>,
                 label: "Заметки",
                 showInMenu: true,
             },
         ],
     },*/

    {
        icon: <HomeRoundedIcon/>,
        path: routes.machineryDetails,
        element: <MachineryDetailsPage/>,
        label: "Подробности",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.addNewMachinery,
        element: <MachineryAddNewPage/>,
        label: "Новая техника",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.machineryAddTask,
        element: <TaskAddNewPage/>,
        label: "Новая проблема",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <HomeRoundedIcon/>,
        path: routes.machineryTaskDetails,
        element: <TaskDetailsPage/>,
        label: "Подробности задачи",
        showInMenu: false,
        requiresAuth: true,
    },
    {
        icon: <BusAlertIcon/>,
        path: routes.errorCodeAddNew,
        element: <AddErrorCodePage/>,
        label: "Кода ошибок",
        showInMenu: false,
        showInDownMenu: false,
        requiresAuth: true,
    },
    {
        icon: <BusAlertIcon/>,
        path: routes.errorCode,
        element: <ErrorCodePage/>,
        label: "Кода ошибок",
        showInMenu: false,
        showInDownMenu: true,
        requiresAuth: true,
    },
    {
        icon: <MenuBookIcon/>,
        path: routes.technicalLiterature,
        element: <TechnicalLiteraturePage/>,
        label: "Тех. литература",
        showInMenu: false,
        showInDownMenu: true,
        requiresAuth: true,
    },
    /*    {
            icon: <HomeRoundedIcon/>,
            path: "*", element: <Navigate to={routes.map}/>,
            label: "Not found",
            showInMenu: false
        },*/
];
